import { AttachmentIcon } from '@chakra-ui/icons'
import {
    Box,
    Button,
    Center,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    ListItem,
    Progress,
    Text,
    Textarea,
    Tooltip,
    UnorderedList,
    useToast,
} from '@chakra-ui/react'
import React, { useEffect, useRef, useState } from 'react'

import { borderVariant33, borderVariant34 } from '../chakraTheme'
import { FILE_UPLOAD_STATUS } from './FileUploadQuestion/_enums'
import { FileIcon, Tick, TrashCan } from './FileUploadQuestion/_icons'
import { convertToMB } from './FileUploadQuestion/_util'
import ImportantStar from './ImportantStar'
import Toast from './Toast'

const FileUploadTextareaInput = ({
    name,
    maxFileSize = 5,
    maxNoOfFiles = 5,
    onFilesChange,
    ...props
}) => {
    const {
        variant = 'retro',
        isRequired = false,
        label,
        errorMessage,
        allowFileUpload = false,
        size = 'md',
        value,
        handleChange,
        ...restProps
    } = props

    const toast = useToast()
    const [files, setFiles] = useState([])
    const fileInputRef = useRef(null)
    const uploadIntervals = useRef({})

    useEffect(() => {
        if (onFilesChange) {
            onFilesChange(files)
        }
    }, [files, onFilesChange])

    const getFileDataAsBase64 = file => {
        return new Promise((resolve, reject) => {
            if (!(file instanceof Blob)) {
                // Check if file is a Blob or File
                console.error('Provided file is not a Blob:', file)
                reject(new TypeError('Parameter is not of type Blob.'))
                return
            }

            const reader = new FileReader()
            reader.readAsDataURL(file)
            reader.onload = () => resolve(reader.result)
            reader.onerror = error => {
                console.error('Error reading file:', error)
                reject(error)
            }
        })
    }

    const checkIfFilesExceedLimit = fileCount => {
        if (fileCount >= maxNoOfFiles) {
            toast({
                duration: 3000,
                position: 'top-right',
                render: props => (
                    <Toast
                        status={'error'}
                        title={
                            <Text>
                                You have reached the maximum number of files you
                                can upload.
                            </Text>
                        }
                        {...props}
                    />
                ),
            })
            return true
        } else {
            return false
        }
    }

    const handleFileUpload = async event => {
        const maxSize = maxFileSize * 1024 * 1024 // Convert to bytes
        const filesExceedingSizeLimit = []

        if (checkIfFilesExceedLimit(event.target.files.length)) {
            return null
        }

        // Create an array of promises for processing each file
        const filePromises = Array.from(event.target.files).map(async file => {
            if (file.size > maxSize) {
                filesExceedingSizeLimit.push(file.name)
                return null // Exclude files exceeding size limit
            }

            try {
                // Convert the file to base64 format
                const fileData = await getFileDataAsBase64(file)

                // Return the processed file object
                return {
                    ...file,
                    name: file.name,
                    data: fileData,
                    size: file.size,
                    type: file.type,
                    status: FILE_UPLOAD_STATUS.UPLOADING,
                    progress: 0, // Assuming the upload is starting
                }
            } catch (error) {
                console.error('Error processing file:', file.name, error)
                return null // Return null if there's an error processing the file
            }
        })

        // Wait for all file promises to resolve
        const newFiles = (await Promise.all(filePromises)).filter(
            file => file !== null
        )

        if (filesExceedingSizeLimit.length > 0) {
            toast({
                duration: 3000,
                position: 'top-right',
                render: props => (
                    <Toast
                        status={'error'}
                        title={
                            <>
                                <Text>
                                    One or more files you are trying to upload
                                    exceeded the maximum file size limit.
                                </Text>
                                <UnorderedList>
                                    {filesExceedingSizeLimit.map(
                                        (fileName, idx) => (
                                            <ListItem key={fileName + idx}>
                                                {fileName}
                                            </ListItem>
                                        )
                                    )}
                                </UnorderedList>
                            </>
                        }
                        {...props}
                    />
                ),
            })
        }

        setFiles(prevFiles => [...prevFiles, ...newFiles])

        // Simulate file upload process
        newFiles.forEach((file, index) => {
            const fileIndex = files.length + index
            const interval = setInterval(() => {
                setFiles(prevFiles => {
                    const updatedFiles = [...prevFiles]
                    if (updatedFiles[fileIndex]) {
                        updatedFiles[fileIndex].progress += 10
                        if (updatedFiles[fileIndex].progress >= 100) {
                            clearInterval(interval)
                            updatedFiles[fileIndex].status =
                                FILE_UPLOAD_STATUS.SUCCESSFUL
                            updatedFiles[fileIndex].progress = 100
                        }
                    }
                    return updatedFiles
                })
            }, 200)

            // Store interval ID in ref
            uploadIntervals.current[fileIndex] = interval
        })

        // Clear file input value to allow re-uploading the same file
        event.target.value = null
    }

    const removeFile = index => {
        // Clear the interval for the file being removed
        if (uploadIntervals.current[index]) {
            clearInterval(uploadIntervals.current[index])
            delete uploadIntervals.current[index]
        }

        // Remove the file from state
        setFiles(prevFiles => prevFiles.filter((_, i) => i !== index))
    }

    const sizeToHeight = {
        sm: '100px',
        md: '180px',
        lg: '220px',
    }

    return (
        <FormControl isInvalid={props.isInvalid} name={props.name}>
            <Box position="relative" mt={6}>
                <FormLabel
                    htmlFor={props.name}
                    variant={variant}
                    className="dark:text-gray-100"
                >
                    {label}
                    {isRequired && (
                        <ImportantStar color="flushmahogany" ml={1} />
                    )}
                </FormLabel>
                <Textarea
                    name={name}
                    variant={variant}
                    height={sizeToHeight[size]}
                    paddingBottom={allowFileUpload ? 14 : 3}
                    position="relative"
                    overflowY="auto"
                    value={value}
                    onChange={handleChange}
                    className="dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 dark:placeholder-gray-400"
                    {...restProps}
                />
                {allowFileUpload && (
                    <Box
                        position="absolute"
                        bottom={0.5}
                        left={0}
                        ml={4}
                        right={5}
                        zIndex={0}
                        p={1}
                        paddingBottom={3}
                        paddingLeft={4}
                        marginLeft={'3px'}
                        backgroundColor="white"
                        overflow="hidden"
                        className="dark:bg-gray-800"
                    >
                        <Text
                            color="rum2"
                            fontSize={'md'}
                            fontStyle="normal"
                            fontWeight={'bold'}
                            lineHeight={6}
                            fontFamily="'Rubik', sans-serif"
                            cursor="pointer"
                            onClick={() => fileInputRef.current.click()}
                            className="dark:text-gray-300"
                        >
                            <AttachmentIcon mr={3} />
                            Attach Files
                        </Text>
                        <Input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            onChange={handleFileUpload}
                            display="none"
                        />
                    </Box>
                )}
            </Box>
            {/* Display File Upload */}
            {files.length > 0 && (
                <Box mt={6}>
                    {files.map((fileObj, index) => (
                        <Flex
                            key={fileObj.name ?? index}
                            mb={2}
                            borderLeft={borderVariant33}
                        >
                            <Center
                                p={3}
                                bg="mischka2"
                                opacity={
                                    [
                                        FILE_UPLOAD_STATUS.SUCCESSFUL,
                                        FILE_UPLOAD_STATUS.PREVIEW,
                                    ].includes(fileObj.status)
                                        ? 'unset'
                                        : 0.6
                                }
                            >
                                <FileIcon w={8} h={8} />
                            </Center>
                            <Flex
                                pl={6}
                                pr={9}
                                w={'full'}
                                bg="alto4"
                                justify={'space-between'}
                                align={'center'}
                                py={2.5}
                            >
                                <Box
                                    opacity={
                                        [
                                            FILE_UPLOAD_STATUS.SUCCESSFUL,
                                            FILE_UPLOAD_STATUS.PREVIEW,
                                        ].includes(fileObj.status)
                                            ? 'unset'
                                            : 0.6
                                    }
                                >
                                    <Text
                                        noOfLines={1}
                                        maxW={'300px'}
                                        fontSize={'sm'}
                                        lineHeight={'120%'}
                                    >
                                        {fileObj.name}
                                    </Text>
                                    <Text
                                        fontSize={'xs'}
                                        color="scorpion"
                                        lineHeight={'120%'}
                                        mt={1.5}
                                    >
                                        {convertToMB(fileObj.size, 'bytes')}
                                    </Text>
                                </Box>
                                <Flex alignItems={'center'}>
                                    {fileObj.status ===
                                        FILE_UPLOAD_STATUS.SUCCESSFUL && (
                                        <>
                                            <Tick
                                                fontSize="md"
                                                color={'fruitsalad'}
                                            />
                                            <Text
                                                fontStyle={'italic'}
                                                color={'fruitsalad'}
                                                fontSize="xs"
                                                ml={2}
                                            >
                                                Uploaded
                                            </Text>
                                        </>
                                    )}
                                    {fileObj.status ===
                                        FILE_UPLOAD_STATUS.UPLOADING && (
                                        <Flex align={'center'}>
                                            <Progress
                                                h={1.5}
                                                variant={'retro3'}
                                                value={fileObj.progress}
                                                w={'100px'}
                                            />
                                            <Text
                                                ml={4}
                                                color="scorpion"
                                                fontSize={'xs'}
                                            >
                                                {fileObj.progress}%
                                            </Text>
                                        </Flex>
                                    )}
                                    {fileObj.status ===
                                        FILE_UPLOAD_STATUS.FAILED && (
                                        <Flex align={'center'}>
                                            <Text
                                                fontStyle={'italic'}
                                                color={'flushmahogany'}
                                                fontSize={'xs'}
                                            >
                                                Failed to upload
                                            </Text>
                                            <Text
                                                as={'button'}
                                                ml={6}
                                                textDecoration={'underline'}
                                                fontSize={'xs'}
                                                onClick={() => {
                                                    // Retry logic (not implemented)
                                                }}
                                            >
                                                Retry
                                            </Text>
                                        </Flex>
                                    )}
                                    <Button
                                        ml={6}
                                        variant={'unstyled'}
                                        minW={0}
                                        height={'auto'}
                                        _hover={{
                                            '&::before': {
                                                content: '" "',
                                                w: '26px',
                                                h: '26px',
                                                bgColor: 'mischka',
                                                position: 'absolute',
                                                border: borderVariant34,
                                                top: -1,
                                                left: -1,
                                            },
                                        }}
                                        position={'relative'}
                                        onClick={() => removeFile(index)}
                                    >
                                        <Tooltip
                                            label={'Delete File'}
                                            placement="right"
                                            shouldWrapChildren
                                            offset={[0, 15]}
                                            variant="retro"
                                        >
                                            <TrashCan
                                                position={'relative'}
                                                top={0}
                                                fontSize={'2xl'}
                                            />
                                        </Tooltip>
                                    </Button>
                                </Flex>
                            </Flex>
                        </Flex>
                    ))}
                </Box>
            )}
            <FormErrorMessage>{errorMessage}</FormErrorMessage>
        </FormControl>
    )
}

export default FileUploadTextareaInput
